import React from "react";
import i18next from "src/i18n/config";
import { MinimalTableCellWithTooltip } from "src/ui/shared/table/MinimalTableComponents";

import { createColumnHelper } from "@tanstack/react-table";

export function parseLines(value: string): string[] {
    return value
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
}

export type OauthClientFormValues = Omit<
    API.OauthClient,
    "redirectUris" | "postLogoutRedirectUris" | "permissions" | "requirements" | "clientSecret"
> & {
    redirectUris: string;
    postLogoutRedirectUris: string;
    permissions: string;
    requirements: string | undefined;
    clientSecret: string | undefined;
};

export function toFormValues(client: API.OauthClient): OauthClientFormValues {
    return {
        ...client,
        clientSecret: client.clientSecret ?? undefined,
        redirectUris: (client.redirectUris ?? []).join("\n"),
        postLogoutRedirectUris: (client.postLogoutRedirectUris ?? []).join("\n"),
        permissions: (client.permissions ?? []).join("\n"),
        requirements: (client.requirements ?? []).join("\n"),
    };
}

export function toApiPayload(values: OauthClientFormValues): API.OauthClient {
    const requirements = values.requirements ?? "";
    return {
        ...values,
        clientSecret: values.clientSecret ?? null,
        redirectUris: parseLines(values.redirectUris),
        postLogoutRedirectUris: parseLines(values.postLogoutRedirectUris),
        permissions: parseLines(values.permissions),
        requirements: requirements.trim() ? parseLines(requirements) : null,
    };
}

const columnHelper = createColumnHelper<API.OauthClient>();

export const columns = [
    columnHelper.accessor("clientId", {
        id: "clientId",
        header: i18next.t("pages:adminOauthClientsPage.clientId"),
        cell: (info) => <MinimalTableCellWithTooltip title={info.getValue()} style={{ maxWidth: 200 }} />,
        enableSorting: false,
    }),
    columnHelper.accessor("displayName", {
        id: "displayName",
        header: i18next.t("common:label.name"),
        cell: (info) => <MinimalTableCellWithTooltip title={info.getValue() ?? ""} style={{ maxWidth: 180 }} />,
        enableSorting: false,
    }),
    columnHelper.accessor("clientType", {
        id: "clientType",
        header: i18next.t("pages:adminOauthClientsPage.clientType"),
        cell: (info) => <MinimalTableCellWithTooltip title={info.getValue() ?? ""} style={{ maxWidth: 150 }} />,
        enableSorting: false,
    }),
    columnHelper.accessor("redirectUris", {
        id: "redirectUris",
        header: i18next.t("pages:adminOauthClientsPage.redirectUris"),
        cell: (info) => <MinimalTableCellWithTooltip title={info.getValue()?.join(", ") ?? ""} style={{ maxWidth: 200 }} />,
        enableSorting: false,
    }),
    columnHelper.accessor("postLogoutRedirectUris", {
        id: "postLogoutRedirectUris",
        header: i18next.t("pages:adminOauthClientsPage.postLogoutRedirectUris"),
        cell: (info) => <MinimalTableCellWithTooltip title={info.getValue()?.join(", ") ?? ""} style={{ maxWidth: 200 }} />,
        enableSorting: false,
    }),
    columnHelper.accessor("permissions", {
        id: "permissions",
        header: i18next.t("pages:adminOauthClientsPage.permissions"),
        cell: (info) => <MinimalTableCellWithTooltip title={info.getValue()?.join(", ") ?? ""} style={{ maxWidth: 200 }} />,
        enableSorting: false,
    }),
    columnHelper.accessor("requirements", {
        id: "requirements",
        header: i18next.t("pages:adminOauthClientsPage.requirements"),
        cell: (info) => <MinimalTableCellWithTooltip title={info.getValue()?.join(", ") ?? ""} style={{ maxWidth: 200 }} />,
        enableSorting: false,
    }),
];
